/**
 * Book 页面业务逻辑 Composable
 * 负责处理书籍详情、章节信息、阅读跳转等所有业务逻辑
 */
import { computed, ref, watch } from 'vue'
import type { Ref } from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'
import type { BookPageConfig } from '../configs/types'
import { superstoryTrack } from '@/track'
import { api } from '@/api'
import { $loadTextFromCDN } from '@/helpers'
import { useToastStore } from '@/stores'
import { useGotoBack } from '@/composables'
import { useBookshelf } from '@/composables/useBookshelf'
import { useAgeGate } from '@/composables/useAgeGate'
import { aliOssImageCompress } from '@/utils/aliOssImageCompress'
import { useElementClick } from '@/composables/tracks/useElementClick'
import { useBookExposure } from '@/composables/tracks/useBookExposure'
import { READER_ROUTE_NAME } from '@/utils/readerRouteName'

export function useBookPage(contentId: Ref<string>, config: BookPageConfig) {
  const route = useRoute()
  const router = useRouter()
  const { $toast } = useToastStore()
  const { elementClick } = useElementClick()
  const { bookExposure } = useBookExposure()
  const gotoBack = useGotoBack()
  const { checkAgeGate } = useAgeGate()

  const currentContentId = ref<string | null>(null)
  const skeleton = ref(false)
  const book = ref<GetContentGetcontentdetailResponse['data']>()
  const sample = ref<string>('')
  const introduce = ref<string>()
  const popupTOC = ref(false)
  const secondChapterId = ref('')

  // 加入书架（与阅读器共享逻辑）
  const { isInShelf, shelfLoading, toggle: onToggleShelf } = useBookshelf(book)

  // 计算属性
  const paragraphs = computed(() => {
    if (!sample.value)
      return []
    return sample.value.split(/[\n]/).filter(Boolean)
  })

  const coverUrl = computed(() => {
    let cover = ''
    if (route.query.picture_id)
      cover = aliOssImageCompress(route.query.picture_id as string)

    return (cover || book.value?.contentCoverUrl) as string | undefined
  })

  /**
   * 获取书籍详情
   */
  async function fetchBook() {
    skeleton.value = true
    const { data } = await api.get<any, GetContentGetcontentdetailResponse>(
      '/content/getContentDetail',
      { params: { contentId: contentId.value } },
    )
    superstoryTrack('book_detail_page_pv', { book_id: data.id, book_name: data.contentName, author: data.authorInfo?.author, column_id: route.query.columnId, column_name: route.query.columnName })
    book.value = data
    isInShelf.value = !!data.bookShelf
    skeleton.value = false
    checkAgeGate(data.ageGate)

    if (data.firstChapterContentUrl)
      sample.value = await $loadTextFromCDN(data.firstChapterContentUrl)
    else
      sample.value = config.texts.noChapters

    bookExposure({
      book_id: data.id,
      book_name: data.contentName,
      author: data.authorInfo.author,
    })

    getCurrentChapter()
  }

  /**
   * 获取当前章节位置
   */
  async function getCurrentChapter() {
    const { data } = await api.post<any, PostReadPosResponse>(
      '/read/pos',
      { contentId: book.value!.id },
    )
    currentContentId.value = data.contentChapterId
  }

  /**
   * 获取引导语
   */
  async function getLeadSentence() {
    const { data } = await api.get<any, GetContentGetLeadSentenceResponse>(
      `/content/getLeadSentence?contentId=${contentId.value}`,
    )
    if (!data)
      return
    introduce.value = data?.replace(/[\n]/g, '<br />')
  }

  /**
   * 开始/继续阅读
   */
  async function onRead() {
    if (!book.value?.firstChapter)
      return $toast(config.texts.noChapters)

    elementClick('read')

    if (currentContentId.value) {
      router.push({
        name: READER_ROUTE_NAME,
        params: { contentId: book.value!.id, chapterId: currentContentId.value },
        query: { skipPosition: '1' },
      })
      return
    }
    router.push({
      name: READER_ROUTE_NAME,
      params: { contentId: book.value!.id, chapterId: book.value.firstChapter!.id },
    })
  }

  /**
   * 上滑触底 → 跳转下一章
   * - 有阅读记录：复用 onRead()（与「Continue」按钮一致）
   * - 无阅读记录：跳第二章，拿不到则回退第一章
   */
  async function onPullUpToNext() {
    if (!book.value?.firstChapter)
      return $toast(config.texts.noChapters)

    if (currentContentId.value) {
      onRead()
      return
    }

    if (!secondChapterId.value) {
      try {
        const { data } = await api.get<any>('/content/getContentChapterBookList', {
          params: { contentId: book.value.id, pageIndex: 1, pageSize: 100 },
        })
        secondChapterId.value = data?.records?.[1]?.id || ''
      }
      catch {}
    }
    const targetId = secondChapterId.value || book.value.firstChapter.id
    router.push({
      name: READER_ROUTE_NAME,
      params: { contentId: book.value.id, chapterId: targetId },
    })
  }

  /**
   * 打开目录弹窗
   */
  function openPopupTOC() {
    popupTOC.value = true
  }

  /**
   * 跳转到标签页
   */
  function gotoTags(tagId: string) {
    router.push({ name: 'tags', query: { tagId } })
  }

  // ========== 初始化和监听 ==========
  getLeadSentence()
  fetchBook()

  watch(contentId, () => {
    getLeadSentence()
    fetchBook()
  })

  return {
    // 状态
    book,
    sample,
    paragraphs,
    introduce,
    currentContentId,
    skeleton,
    popupTOC,
    coverUrl,
    isInShelf,
    shelfLoading,

    // 方法
    fetchBook,
    getCurrentChapter,
    getLeadSentence,
    onRead,
    onPullUpToNext,
    onToggleShelf,
    openPopupTOC,
    gotoTags,
    gotoBack,
  }
}
